/*
 * Copyright 2025 coze-dev Authors
 * Modifications Copyright 2026 Bytefinger Technology (HK) Co., Limited (https://bytefinger.ai)
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { setUserInfoContext } from '@adp-arch/logger';
import {
  advanceAuthGeneration,
  getAuthSnapshot,
  getPendingUnauthorizedGeneration,
} from '@adp-arch/bot-http';

import { type UserInfo } from '../types';
import { useUserStore } from '../store/user';

/** Commit a successful login, even when it authenticates the same user again. */
export const loginBase = (userInfo: UserInfo) => {
  useUserStore.getState().authenticate(userInfo);
  setUserInfoContext(userInfo);
  if (typeof BroadcastChannel !== 'undefined') {
    const channel = new BroadcastChannel('adp-auth-lifecycle-v1');
    channel.postMessage({ type: 'invalidate', pendingLogout: false });
    channel.close();
  }
};

/**
 * Actively trigger to refresh user information
 * @param checkLogin check function
 */
export const refreshUserInfoBase = async (
  checkLogin: () => Promise<UserInfo>,
) => {
  const { generation } = getAuthSnapshot();
  useUserStore.setState({
    hasError: false,
  });
  const userInfo = await checkLogin();
  if (generation !== getAuthSnapshot().generation) {
    return;
  }
  useUserStore.getState().setUserInfo(userInfo);
};

export const logoutBase = async (logout: () => Promise<void>) => {
  const { generation } = getAuthSnapshot();
  await logout();
  if (generation === getAuthSnapshot().generation) {
    useUserStore.getState().reset();
  }
};

export const checkLoginBase = async (
  checkLoginImpl: () => Promise<{
    userInfo?: UserInfo;
    hasError?: boolean;
  }>,
) => {
  const { generation } = getAuthSnapshot();
  useUserStore.setState({
    hasError: false,
  });
  const { userInfo, hasError } = await checkLoginImpl();
  // A bootstrap result is not a fresh login and cannot consume a pending 401.
  if (
    generation !== getAuthSnapshot().generation ||
    getPendingUnauthorizedGeneration() === generation
  ) {
    return;
  }
  if (hasError) {
    useUserStore.setState({
      hasError: true,
    });
    return;
  }
  if (userInfo) {
    setUserInfoContext(userInfo);
  }
  useUserStore.setState({
    userInfo,
    isSettled: true,
  });
  advanceAuthGeneration(Boolean(userInfo?.user_id_str));
};
